import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import classNames from 'classnames';
import Card from 'src/components/common/card/Card';
import Typography from 'src/components/common/typography/Typography';
import { textFormatter, transformTextToHyperlink } from 'src/utils';
import { BookmarkIcon, MoreOptionsIcon, SpeakerIcon } from 'src/components/common/common.icons';
import ProfilePicture from 'src/components/profile/profilePicture/ProfilePicture';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import Button from 'src/components/button/Button';
import SubMenuModal from 'src/components/common/subMenuModal/SubMenuModal';
import { assignSubMenuModalId, openPopup } from 'src/store/common/common.slice';
import {
  Carousel,
  Comment,
  ConfirmModal,
  Dropdown,
  Like,
  MediaModal,
  TCarouselItem,
  TDropdown
} from 'src/components/common';
import { TDropdownOptions } from 'src/components/common/dropdown/Dropdown';
import {
  TFollowRequest,
  TLikePostRequest,
  TPost,
  TSavePostRequest,
  TUnFollowRequest,
  TUnSavePostRequest
} from 'src/services/apiEndpoint.types.ts';
import {
  follow,
  getLikesUser,
  getPoll,
  likePost,
  savePost,
  setNewsFeed,
  setNewsFeedState,
  unFollow,
  unLikePost,
  unSavePost,
  updateNewsFeedState
} from 'src/store/socialMedia/socialMedia.slice';
import { useCopyToClipboard, useGetIdentity, useOpenInNewTab } from 'src/hooks';
import { TUserTypes } from 'src/constants/user.constants';

import './postCard.scss';
import Share from 'src/components/common/share/Share';
import LinkCard from 'src/components/cards/LinkCard/LinkCard';
import axios from 'axios';
import getRelativeTime from 'src/components/common/relativeTime/relativeTime';
import PollCard from '../PollCard/PollCard';
import Entrepreneur from 'src/assets/images/svg/Entrepreneur.svg';
import Company from 'src/assets/images/svg/Company.svg';
import IncubatorBadge from 'src/assets/images/svg/IncubatorBadge.svg';
import MentorBadge from 'src/assets/images/svg/MentorBadge.svg';
import { getMetaData } from 'src/store/chat/chat.slice';
import LikeUserModal from 'src/components/common/LikeUserModal/LikeUserModal';
import { log } from 'console';
import addHttps from 'src/utils/addhttps/addhttps';
import { socketService } from 'src/services/socket.api.service';

export type TPostCardProps = {
  dropdownItems: TDropdownOptions[];
  highlightComment?: boolean;
  onCommentClick: () => void;
  post: TPost;
  viewerId: string;
  viewerType: TUserTypes;
  handleFollowFromSearchResult?: () => void;
};
export type PollOption = {
  id: string;
  text: string;
  voteCount: number;
  percentage: number;
  hasVoted: boolean;
};

export type Poll = {
  id: string;
  postId: string;
  question: string;
  options: PollOption[];
  totalVotes: number;
  hasVoted: boolean;
  allowMultipleValues: boolean;
  createdAt: string;
};
export type TLikeUser = { name: string, profilePic?: string, logo?: string, role?: string, userId?: string, id?: string, type?: string }

// This component should know its parent. either dashboard or single post or activities
// This component must know id and type of the viewer
const PostCard = ({
  dropdownItems,
  highlightComment = false,
  onCommentClick,
  viewerId,
  viewerType,
  post,
  handleFollowFromSearchResult
}: TPostCardProps) => {
  const { dropdownId } = useAppSelector((store: any) => store.common);
  const isAuthorized: boolean = useAppSelector((store) => store.auth.isAuthorized);
  const { newsFeedState } = useAppSelector((store) => store.socialMedia);
  const { profile } = useAppSelector((store) => store.profile);
  const { myCompany } = useAppSelector((store) => store.company);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { copyToClipboard } = useCopyToClipboard();
  const { openInNewTab } = useOpenInNewTab();
  const { getIdentities } = useGetIdentity();

  const { loggedInCompanyId, loggedInUserId, authenticatedId, loggedInUserType } = getIdentities();

  // state
  const [show, setShow] = useState<boolean>(false)
  const [contentParts, setContentParts] = useState<(string | { link: string })[]>([]);
  const [showMore, setShowMore] = useState(false); // Manage "Read More" state
  const [truncatedParts, setTruncatedParts] = useState<(string | { link: string })[]>([]);
  const [pollData, setPollData] = useState<Poll | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [likeLoading, setLikeLoading] = useState<boolean>(false);
  const [currIndex, setCurrentIndex] = useState<number>(0)
  const [isMediaModalOpen, setIsMediaModalOpen] = useState<boolean>(false);

  // constant and derived state
  const postState = newsFeedState?.[post?.post?.id];
  const url = `${location.pathname}${location.search}`
  const postCreatorId = post?.user.id;
  const following: boolean = post.followUserOrCompany;
  const postSaved: boolean = postState?.savedPost;
  const isViewerCreatorOfPost = viewerId === postCreatorId;
  const isAnnouncement = post?.post?.isAnnouncement;
  const postCreatorType = post?.userType?.[0] || 'Company' as TUserTypes;
  const postCreatorProfilePicture =
    postCreatorType === 'User' ? post?.user?.profilePic : post?.user?.logo;
  const postCreatorName = post?.user?.name;
  const announcementTitle = post?.post?.title;
  // Whether this profile card is viewed by self.
  const isSelfUser =
    viewerType === 'User' ? postCreatorId === loggedInUserId : postCreatorId === loggedInCompanyId;
  const dropdownOptions: TDropdown = {
    id: `postcard-dropdown=${post.post.id}`,
    size: 'large',
    alignment: 'center',
    items: dropdownItems
  };

  const isContainAvailable = (showMore && contentParts?.join('') !== ' ') || (!showMore && truncatedParts?.join('') !== ' ')
  const profilePictureUrl = useMemo(() => {
    return postCreatorProfilePicture ? `${postCreatorProfilePicture}?t=${Date.now()}` : '';
  }, [postCreatorProfilePicture]);
  const postMoreOptions = () => {
    if (dropdownId) {
      dispatch(assignSubMenuModalId(''));
    } else {
      dispatch(assignSubMenuModalId(dropdownOptions.id));
    }
  };
  const media: TCarouselItem[] = useMemo(() => {
    try {
      const urls = post?.post?.urls;
      if (urls) {
        return (JSON.parse(post?.post?.urls || '') || []) as unknown as TCarouselItem[];
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  const viewUserProfile = () => {
    if (isViewerCreatorOfPost) {
      if (location.pathname !== '/my-company' && location?.pathname !== '/my-profile') {
        navigate(postCreatorType === 'User' ? '/my-profile' : '/my-company');
      }
    } else {
      navigate(
        `${postCreatorType === 'User' ? '/view-profile' : '/view-company'}?id=${postCreatorId}&prev=${location.pathname}?postId=${post?.post?.id}`
      );
    }
  };
  const handleLikePost = () => {
    if (!isAuthorized) {
      setShow(true)
      return
    }
    // if (likeLoading) {
    //   return
    // }
    setLikeLoading(true)
    const payload: TLikePostRequest = {
      userId: viewerId,
      userType: viewerType,
      postId: post?.post?.id
    };
    const modifiedLikedByUsers = !postState ? [] : [...postState?.likedByUsers, {
      ...(loggedInUserType === 'User' ? { ...profile, userType: [loggedInUserType] } : { ...myCompany, userType: [loggedInUserType] })
    }]
    const stateObject = {
      ...postState,
      likeCount: postState?.likeCount ? postState.likeCount + 1 : 1,
      likedByUser: !postState?.likedByUser,
      likedByUsers: modifiedLikedByUsers
    }
    dispatch(updateNewsFeedState(stateObject))
    dispatch(likePost(payload)).then((response) => {
      // const existingLikedUser = [...likedUser];
      // if (loggedInUserType === 'Company') {
      //   existingLikedUser.push(myCompany)
      // } else {
      //   existingLikedUser.push(profile)
      // }
      // setLikedUser(existingLikedUser)
      // handleLikedUser()

      const performAction = (userId, userType) => {
        const userIds = [userId];
        if (userId !== authenticatedId) {
          socketService.emit("notifications-unread", {
            userIds,
            userType: userType
          });
        }
      };
      performAction(post.user.id, post.userType[0]);
    }).finally(() => {
      setLikeLoading(false)
    });
  };
  const handleFollow = () => {
    if (!isAuthorized) {
      setShow(true)
      return
    }
    const followUserType = post?.userType[0] as 'User' | 'Company';
    const followUserId = post?.user?.id;

    if (handleFollowFromSearchResult) {
      handleFollowFromSearchResult();
    }
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
    }, 1800)
    if (following) {
      const payload: TUnFollowRequest = {
        userId: viewerId,
        userType: viewerType,
        followUserId,
        followUserType
      };

      dispatch(unFollow(payload)).then(() => {
        dispatch(
          openPopup({
            popupMessage: `You have stopped following ${post?.user?.name}`,
            popupType: 'success'
          })
        );
      });
    } else {
      const payload: TFollowRequest = {
        userId: viewerId,
        userType: viewerType,
        followUserId,
        followUserType
      };
      dispatch(follow(payload)).then(() => {
        // Call performAction only after the follow action succeeded
        const performAction = (userId, userType) => {
          const userIds = [userId];
          socketService.emit("notifications-unread", {
            userIds,
            userType: userType
          });
        };
        performAction(followUserId, followUserType);
      });
      // dispatch(follow(payload));
      // // hashtag fool
      // const performAction = (userId, userType) => {
      //   const userIds = [userId];
      //   socketService.emit("notifications-unread", {
      //     userIds,
      //     userType: userType
      //   });
      // };
      // performAction(followUserId, followUserType)
    }
  };

  const handleUnLikePost = () => {
    if (!isAuthorized) {
      setShow(true)
      return
    }
    // if (likeLoading) {
    //   return
    // }
    const payload: TLikePostRequest = {
      userId: viewerId,
      userType: viewerType,
      postId: post?.post?.id
    };
    const modifiedLikedByUsers = postState?.likedByUsers?.filter((item) => item?.id !== authenticatedId)
    const stateObject = {
      ...postState,
      likeCount: postState?.likeCount ? postState.likeCount - 1 : 0,
      likedByUser: !postState?.likedByUser,
      likedByUsers: modifiedLikedByUsers
    }
    dispatch(updateNewsFeedState(stateObject))
    setLikeLoading(true)
    dispatch(unLikePost(payload)).then((response) => {
      // let existingLikedUser = [...likedUser];
      // existingLikedUser = existingLikedUser.filter((item) => item?.userId !== authenticatedId);
      // setLikedUser(existingLikedUser)
      // handleLikedUser()

    }).finally(() => {
      setLikeLoading(false)
    });
  };
  const openMediaModal = () => {
    setIsMediaModalOpen(true);
  };

  const handleSavePost = () => {
    if (!isAuthorized) {
      setShow(true)
      return
    }
    if (!postSaved) {
      const payload: TSavePostRequest = {
        userId: viewerId,
        userType: viewerType,
        postId: post?.post?.id
      };
      const stateObject = {
        ...postState,
        savedPost: !postSaved
      }
      dispatch(updateNewsFeedState(stateObject))
      dispatch(savePost(payload));
    } else {
      const payload: TUnSavePostRequest = {
        userId: viewerId,
        userType: viewerType,
        postId: post?.post?.id
      };
      const stateObject = {
        ...postState,
        savedPost: !postSaved
      }
      dispatch(updateNewsFeedState(stateObject))
      dispatch(unSavePost(payload));
    }
  };
  const handleRedirectionToUrl = () => {
    const url: any = post?.post?.redirectUrl;
    if (url) {
      if (url && !url?.startsWith('http://') && !url?.startsWith('https://')) {
        const urlToOpen = 'https://' + url;
        openInNewTab({ url: urlToOpen });
      } else {
        openInNewTab({ url });
      }
    }
  };
  function handleNavigate() {
    navigate(`/login?prev=${url}`)
  }
  function handleShare() {
    copyToClipboard({ text: `${window.location.host}/view-post?postId=${post?.post?.id}` });
    dispatch(
      openPopup({
        popupMessage: 'Post link copied successfully!',
        popupType: 'success'
      })
    );
  }
  const ProfileHead: ReactNode = (
    <div className="post-card__header__user" onClick={viewUserProfile}>
      <ProfilePicture
        url={profilePictureUrl}
        fallback={textFormatter({ value: postCreatorName?.[0] })}
        className="post-card__header__user__picture"
        userType={post?.userType?.[0]}
      />
      <div className="post-card__header__user__details">
        <Typography
          as="p"
          variant="body-1"
          fontWeight="semiBold"
          className="post-card__header__user__details__name"
        >
          {textFormatter({ value: postCreatorName || '' })}
          {(post.user?.role === "Aspiring Entrepreneur" || post.user?.role === "Entrepreneur") && (<img style={{ marginLeft: "8px", height: "18px", width: "18px", marginTop: "2px" }} src={Entrepreneur} alt="Entrepreneur" />)}
          {(post.user?.role === "Mentor" || post.user?.role === "Investor") && (<img style={{ marginLeft: "8px", height: "18px", width: "18px", marginTop: "2px" }} src={MentorBadge} alt="Mentor/Investor" />)}
          {(post.user?.type === "Service Provider" || post.user?.type === "Start up") && (<img style={{ marginLeft: "8px", height: "18px", width: "18px", marginTop: "2px" }} src={Company} alt="Company" />)}
          {(post.user?.type === "Incubator" || post.user?.type === "Accelerator") && (<img style={{ marginLeft: "8px", height: "18px", width: "18px", marginTop: "2px" }} src={IncubatorBadge} alt="Incubator/Accelerator" />)}
        </Typography>
        <Typography
          as="p"
          variant="caption"
          fontWeight="regular"
          className="post-card__header__user__details__role"
        >
          {(post.user?.role !== "Aspiring Entrepreneur" && post.user?.role !== "Entrepreneur") && (<span>{textFormatter({ value: post.user?.type || post.user?.role })}</span>)} {(post.user?.role !== "Aspiring Entrepreneur" && post.user?.role !== "Entrepreneur") && (<span>{" > "}</span>)} <span>{getRelativeTime(post.post.timestamp)}</span>
        </Typography>
      </div>
    </div>
  );

  // const fetchMetadata = async (url: string) => {
  //   try {
  //     const data = await dispatch(getMetaData({ url }));
  //     // const response = await axios.get(`https://api.microlink.io?url=${encodeURIComponent(url)}`);
  //     if (data?.status) {
  //       return { ...data?.data, image: data?.data?.image ? { url: data?.data?.image } : null }
  //     }
  //   } catch (error) {
  //     console.error('Failed to fetch metadata:', error);
  //     return null;
  //   }
  // };
  const fetchMetadata = async (url: string) => {
    try {
      const response = await axios.get(`https://api.microlink.io?url=${encodeURIComponent(url)}`);
      if (response?.status === 200) {
        return response.data.data;
      }
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
      return null;
    }
  };

  const extractLinksFromText = (input: any): (string | { link: string })[] => {
    // const urlRegex = /(\b(?:https?:\/\/)?(?:www\.)?[\w.-]+\.[a-zA-Z]{2,}\/?[\w/?=&-]*)/g;
    const urlRegex =
      /\b((http(s)?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?\b/g;

    const result = [];
    let lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(input)) !== null) {
      const index = match.index;

      // Push any text before the URL
      if (lastIndex < index) {
        result.push(input.slice(lastIndex, index));
      }

      // Push the full matched URL
      result.push({ link: match[0] });

      lastIndex = index + match[0].length;
    }

    // Push any remaining text after the last URL
    if (lastIndex < input.length) {
      result.push(input.slice(lastIndex));
    }

    return result;
  };

  const truncateContent = (parts: (string | { link: string })[], limit: number) => {
    let currentLength = 0;
    const result: (string | { link: string })[] = [];

    for (const part of parts) {
      if (typeof part === 'string') {
        if (currentLength + part.length > limit) {
          result.push(part.slice(0, limit - currentLength) + '...');
          break;
        }
        result.push(part);
        currentLength += part.length;
      } else if (part?.link) {
        if (currentLength + part.link.length > limit) {
          result.push(part); // Include the full link but stop truncating
          break;
        }
        result.push(part);
        currentLength += part.link.length;
      }
    }
    return result;
  };

  function handlePoll() {
    dispatch(getPoll({ postId: post.post.id, userId: viewerId })).then((response: { data: Poll, status: boolean, message: string }) => {
      setPollData(response?.data)
    })
  }
  const [userStateLoading, setUserStateLoading] = useState<boolean>(false)

  useEffect(() => {
    const text = post?.post?.text || '';
    const parsedContent = extractLinksFromText(text);

    setContentParts(parsedContent);

    const fetchMetadataForLinks = async () => {
      const updatedParts = await Promise.all(
        parsedContent.map(async (part: any) => {
          if (typeof part === 'object' && part?.link) {
            const normalizedUrl = part?.link?.startsWith('http') ? part?.link : `https://${part?.link}`;
            const metadata = await fetchMetadata(normalizedUrl);
            return metadata ? { ...part, metadata } : part?.link; // Replace with link if no metadata
          }
          return part;
        })
      );

      setContentParts(updatedParts);
      setTruncatedParts(truncateContent(updatedParts, 150));
    };
    fetchMetadataForLinks();
  }, [post?.post?.text, viewerId]);
  useEffect(() => {
    if (post?.post?.isPoll && viewerId) {
      handlePoll()
    }
  }, [post?.post?.isPoll, viewerId])
  const linkObj: any = useMemo(() => {
    return contentParts?.find((item: any) => typeof item === 'object');
  }, [contentParts]);
  return (
    <>
      {isModalOpen && <LikeUserModal title="Likes" users={postState?.likedByUsers || []} isModalOpen={isModalOpen} handleCloseModal={() => setIsModalOpen(false)} />}
      <Card className="post-card">
        {isAnnouncement && (
          <div className="post-card__announcement">
            <SpeakerIcon />
            <Typography as="span" variant="body-1" fontWeight="semiBold" className="post-card__announcement__labelling">
              Announcement!
            </Typography>
          </div>
        )}
        <div className="post-card__header">
          {ProfileHead}
          <div className="post-card__header__actions">
            {!isSelfUser && !isAnnouncement && (
              <Button
                variant={following ? 'secondary' : 'primary'}
                size="extraSmall"
                disabled={loading}
                onClick={handleFollow}
              >
                {following ? 'Following' : 'Follow'}
              </Button>
            )}
            {isAnnouncement && post?.post?.customButton && (
              <Button variant={'primary'} size="extraSmall" onClick={handleRedirectionToUrl}>
                {post?.post?.customButton}
              </Button>
            )}
            <div
              className="post-card__header__actions__dropdown"
              onClick={(e) => {
                e.stopPropagation();
                postMoreOptions();
              }}
            >
              {<MoreOptionsIcon />}{' '}
              <SubMenuModal>
                <Dropdown {...dropdownOptions} />
              </SubMenuModal>
            </div>
          </div>
        </div>
        {isAnnouncement && announcementTitle && (
          <Typography
            as="p"
            variant="body-2"
            fontWeight="semiBold"
            className="post-card__announcement-title"
          >
            {announcementTitle}
          </Typography>
        )}
        {isContainAvailable && <div>
          {(showMore ? contentParts : truncatedParts).map((part: any, index: number) => {
            if (typeof part === 'object' && part?.link) {
              return (
                <a
                  key={index}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={part?.link}
                  href={addHttps(part?.link)}
                  style={{ color: '#fa4616d4' }}>
                  {part?.link?.length > part?.link?.slice(0, 40)?.length ? part?.link?.slice(0, 40) + '...' : part?.link}
                </a>
              );
            }

            return (
              <Typography
                style={part === '' ? { display: 'none' } : {}}
                key={index}
                as="span"
                variant="body-3"
                fontWeight="regular"
                className="post-card__sample"
              >
                {part}
              </Typography>
            );
          })}

          {((contentParts?.length > 1 && (contentParts.length > truncatedParts.length || contentParts?.some((item, i: number) => truncatedParts?.[i] !== item))) || (contentParts?.length === 1 && (showMore ? contentParts : truncatedParts)?.every((part) => typeof part === 'string' && part?.length > 150))) && (
            <span
              className="post-card__read-more-button"
              onClick={() => setShowMore((prev) => !prev)}
            >
              ...Read {showMore ? 'Less' : 'More'}
            </span>
          )}
          {linkObj?.metadata && post?.post?.linkPreview && (
            <LinkCard
              metadata={linkObj?.metadata || null}
              link={linkObj?.link}
            />
          )}
        </div>}
        {post.post.isPoll && pollData && (
          <PollCard key={post.post.id} pollData={pollData} viewerId={viewerId} viewerType={viewerType} handlePoll={handlePoll} />
        )}
        {media?.length > 0 && (
          <div className="post-card__carousel">
            <Carousel items={media} openMediaModal={openMediaModal} setCurrentIndex={setCurrentIndex} />
          </div>
        )}

        <div className="post-card__likeCommentList">
          <ul>
            <li onClick={() => postState?.likedByUsers?.length > 0 && setIsModalOpen(true)} style={{ cursor: postState?.likeCount > 0 ? 'pointer' : 'default' }}>{postState?.likeCount || 0} likes</li>
            <li onClick={onCommentClick}>{postState?.commentCount || 0} comments</li>
          </ul>
        </div>

        <div className="post-card__interactions">
          <div className="post-card__interactions__left">
            <Like
              count={0}
              onLike={handleLikePost}
              onUnLike={handleUnLikePost}
              active={isAuthorized ? postState?.likedByUser : false}
              loading={userStateLoading}
            />
            <button
              aria-controls={post?.post?.id}
              aria-expanded={highlightComment}
              className='post-card__interactions__left__comment-button'
            >
              <Comment
                count={0}
                onClick={onCommentClick}
                isActive={highlightComment}
              />

            </button>
            <Share
              onClick={handleShare}
            />
          </div>
          <div
            className={classNames('post-card__interactions__right', {
              'post-card__interactions__right--is-active': postSaved
            })}
            onClick={handleSavePost}
          >
            <BookmarkIcon /> <span style={{ paddingLeft: '3px', fontSize: '13px', color: '#6e6e6e', fontFamily: '"Manrope", sans-serif', fontWeight: '600' }}>Save</span>
          </div>
        </div>
      </Card >
      <ConfirmModal
        title=""
        description={<p style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', margin: '0' }}>
          <span>To enjoy all the features of <span style={{ color: '#fa4616' }}>htraction</span> </span>
          <span><span style={{ color: '#fa4616' }}>Sign in</span> to Continue</span>
        </p>}
        openConfirmModal={show}
        onClose={() => setShow(false)}
        onSubmit={handleNavigate}
        submitText="Sign in"
        type="confirm"
      />
    </>
  );
};

export default PostCard;
